import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth.store';
import { loginApi, fetchMe, verify2faLogin, recover2faLogin } from '@/lib/api';
import { ApiClientError } from '@goodchat/api-client';

function formatLockoutError(err: ApiClientError): string {
  if (err.code === 'ACCOUNT_LOCKED' && err.details?.retryAfter) {
    const seconds = err.details.retryAfter as number;
    const minutes = Math.ceil(seconds / 60);
    return `Account temporarily locked due to too many failed attempts. Please try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
  }
  return err.message;
}

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setOrgs = useAuthStore((s) => s.setOrgs);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState<string>('totp');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  async function completeAuth(res: {
    user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null };
    accessToken: string;
    refreshToken: string;
  }) {
    setAuth(res.user, res.accessToken, res.refreshToken);
    const me = await fetchMe();
    setOrgs(me.orgs);
    navigate({ to: '/' });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await loginApi(email, password);

      if ('twoFactorRequired' in res && res.twoFactorRequired) {
        setTwoFactorToken(res.twoFactorToken);
        setTwoFactorMethod(res.method);
        setLoading(false);
        return;
      }

      await completeAuth(res as { user: { id: string; fullName: string; displayName: string; profilePictureUrl: string | null; homeCity: string | null }; accessToken: string; refreshToken: string });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(formatLockoutError(err));
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleTwoFactorSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let res;
      if (useRecoveryCode) {
        res = await recover2faLogin(twoFactorToken, twoFactorCode);
      } else {
        res = await verify2faLogin(twoFactorToken, twoFactorCode);
      }
      await completeAuth(res);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(formatLockoutError(err));
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // 2FA verification form
  if (twoFactorToken) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight">Two-Factor Authentication</h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {useRecoveryCode
                ? 'Enter one of your recovery codes'
                : twoFactorMethod === 'sms'
                  ? 'Enter the 6-digit code sent to your phone'
                  : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                {error}
              </div>
            ) : null}

            <Input
              id="twoFactorCode"
              label={useRecoveryCode ? 'Recovery Code' : 'Verification Code'}
              type="text"
              autoComplete="one-time-code"
              required
              autoFocus
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder={useRecoveryCode ? 'XXXX-XXXX-XXXX' : '000000'}
              maxLength={useRecoveryCode ? 14 : 6}
            />

            <Button type="submit" loading={loading} className="w-full">
              Verify
            </Button>
          </form>

          <div className="space-y-2 text-center text-sm">
            <button
              type="button"
              onClick={() => {
                setUseRecoveryCode(!useRecoveryCode);
                setTwoFactorCode('');
                setError('');
              }}
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
            >
              {useRecoveryCode ? (twoFactorMethod === 'sms' ? 'Use SMS code instead' : 'Use authenticator app instead') : 'Use a recovery code'}
            </button>
            <p>
              <button
                type="button"
                onClick={() => {
                  setTwoFactorToken('');
                  setTwoFactorCode('');
                  setUseRecoveryCode(false);
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-400"
              >
                Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal login form
  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Sign in to your GoodChat account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
              {error}
            </div>
          ) : null}

          <Input
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />

          <Input
            id="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your password"
          />

          <Button type="submit" loading={loading} className="w-full">
            Sign in
          </Button>
        </form>

        <div className="space-y-2 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>
            Have an invite?{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
              Create an account
            </Link>
          </p>
          <p>
            External guest?{' '}
            <Link to="/guest-login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
              Use invite token
            </Link>
          </p>
          <p className="pt-2">
            <Link to="/privacy" className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
              Privacy
            </Link>
            <span className="mx-2">&middot;</span>
            <Link to="/terms" className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
              Terms
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
